import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Save, Loader2 } from "lucide-react";

const FIELDS = [
  ["default_cruise_altitude_m", "Altitude déplacement standard", "m"],
  ["rtl_altitude_m", "Altitude RTL", "m"],
  ["default_cruise_speed_mps", "Vitesse croisière standard", "m/s"],
  ["surveillance_speed_mps", "Vitesse surveillance", "m/s"],
  ["rapid_response_speed_mps", "Vitesse intervention rapide", "m/s"],
  ["min_battery_for_mission_percent", "Batterie min. mission", "%"],
  ["default_loiter_minutes", "Durée stationnaire défaut", "min"],
  ["destination_radius_m", "Rayon sécurité destination", "m"],
  ["max_mission_distance_km_v1", "Rayon max mission V1", "km"],
  ["min_altitude_m", "Hauteur minimum", "m"],
  ["max_altitude_m", "Hauteur maximum", "m"],
];

export default function FlightSettings() {
  const [settings, setSettings] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/flight-settings").then(({ data }) => { setSettings(data.settings); setProfiles(data.profiles); }).catch(() => {});
  }, []);

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v === "" ? "" : Number(v) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/flight-settings", settings);
      setSettings(data.settings);
      toast.success("Paramètres enregistrés");
    } catch (e) { toast.error("Échec", { description: apiErr(e) }); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="p-6 font-mono text-sm text-[#525252]">Chargement...</div>;

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-chivo text-3xl font-black tracking-tighter uppercase">Paramètres de vol</h1>
          <p className="font-mono text-xs text-[#A3A3A3] mt-1">Valeurs globales réutilisées par le générateur de mission</p>
        </div>
        <button data-testid="settings-save" onClick={save} disabled={saving}
          className="flex items-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-50 text-white font-mono text-xs uppercase tracking-wider rounded-sm px-4 py-2.5">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {FIELDS.map(([k, label, unit]) => (
          <div key={k} className="bg-[#141414] border border-white/10 rounded-sm p-3">
            <label className="font-mono text-[9px] tracking-[0.15em] uppercase text-[#A3A3A3] block mb-1.5">{label}</label>
            <div className="flex items-center gap-2">
              <input data-testid={`setting-${k}`} type="number" value={settings[k]} onChange={(e) => set(k, e.target.value)}
                className="w-full bg-[#0A0A0A] border border-white/15 focus:border-[#3B82F6] outline-none rounded-sm px-2 py-1.5 font-mono text-sm" />
              <span className="font-mono text-[10px] text-[#525252]">{unit}</span>
            </div>
          </div>
        ))}
      </div>

      <div>
        <h2 className="font-chivo text-lg font-bold uppercase tracking-tight mb-3">Profils de mission</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {profiles.map((p) => (
            <div key={p.id} data-testid={`profile-card-${p.id}`} className="bg-[#141414] border border-white/10 rounded-sm p-4">
              <div className="font-chivo font-bold tracking-tight mb-2">{p.label}</div>
              <div className="space-y-1 font-mono text-xs text-[#A3A3A3]">
                <div>Altitude : <span className="text-white">{p.altitude} m</span></div>
                <div>Vitesse : <span className="text-white">{p.speed} m/s</span></div>
                <div>Arrivée : <span className="text-white">{p.arrival_action}</span></div>
                <div>Loiter : <span className="text-white">{p.loiter_minutes} min</span></div>
                <div>Retour base : <span className="text-white">{p.return_home_after ? "oui" : "non"}</span></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
